import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, BarChart3, Users, Factory, TrendingUp, TrendingDown, PlusCircle, MinusCircle, Settings2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, qty, rp } from "@/lib/api";
import { Loading, StatCard, StatusBadge, SectionTitle, EmptyState } from "@/components/common";
import { TxRow } from "@/components/History";

const QUICK = [
  { to: "/pemasukan", label: "Catat Pemasukan", icon: PlusCircle, bg: "#1f7a46", id: "quick-income" },
  { to: "/pengeluaran", label: "Catat Pengeluaran", icon: MinusCircle, bg: "#d9a21b", id: "quick-expense" },
  { to: "/kasbon-pabrik", label: "Catat Kasbon Pabrik", icon: Factory, bg: "#2357c9", id: "quick-sup" },
  { to: "/kasbon-pelanggan", label: "Catat Kasbon Pelanggan", icon: Users, bg: "#6d4fd8", id: "quick-cust" },
];

export default function Beranda() {
  const { ws, version } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e))); }, [version]);
  const mats = Object.fromEntries((ws?.materials || []).map((m) => [m.key, m]));
  return (
    <div className="space-y-6" data-testid="beranda-page">
      <div className="relative overflow-hidden rounded-3xl jt-card jt-fade">
        <img src="/hero-tahu.jpg" alt="" aria-hidden className="absolute right-0 top-0 h-full w-1/2 sm:w-[45%] object-cover opacity-90" style={{ maskImage: "linear-gradient(to right, transparent, black 45%)", WebkitMaskImage: "linear-gradient(to right, transparent, black 45%)" }} />
        <div className="relative p-6 sm:p-8 max-w-[62%] sm:max-w-[60%]">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[var(--jt-emerald)] tracking-tight" data-testid="dashboard-greeting">Selamat Datang, Juragan Tahu!</h2>
          <p className="mt-1.5 text-sm sm:text-base text-muted-foreground">Ini ringkasan usaha tahu Anda hari ini.</p>
          {ws?.business_name && <p className="mt-3 text-sm font-semibold text-[#8a6212]" data-testid="dashboard-business-name">{ws.business_name}</p>}
        </div>
      </div>
      {!ws?.setup_done && (
        <Link to="/pengaturan" className="flex items-center gap-3 jt-card p-4 hover:shadow-lg transition-shadow" data-testid="setup-reminder">
          <Settings2 className="w-5 h-5 text-[var(--jt-gold)]" />
          <span className="text-sm flex-1"><b>Atur usaha Anda (opsional).</b> Isi nama usaha, saldo awal Uang Kas, dan stok awal bahan baku kapan saja.</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
      {err ? <EmptyState text={err} /> : !d ? <Loading /> : (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <StatCard big icon={Wallet} label="Uang Kas" value={rp(d.cash)} hint="Saldo uang berdasarkan catatan transaksi." testid="dash-cash" />
            <StatCard big icon={BarChart3} color="#b7861b" label="Uang Kas Setelah Kasbon" value={rp(d.after_credit)} hint="Simulasi jika semua kasbon pelanggan diterima dan semua kasbon pabrik dilunasi." testid="dash-after-credit" className="bg-[#fffaf0]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard icon={Users} color="#c0392b" label="Kasbon Pelanggan Belum Dibayar" value={rp(d.cust_credit)} testid="dash-cust-credit" />
            <StatCard icon={Factory} color="#b7861b" label="Kasbon Pabrik Belum Dilunasi" value={rp(d.sup_credit)} testid="dash-sup-credit" />
            <StatCard icon={TrendingUp} color="#1f7a46" label="Pemasukan Hari Ini" value={rp(d.income_today)} hint={`${d.income_today_count} transaksi`} testid="dash-income-today" />
            <StatCard icon={TrendingDown} color="#c0392b" label="Pengeluaran Hari Ini" value={rp(d.expense_today)} hint={`${d.expense_today_count} transaksi`} testid="dash-expense-today" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK.map(({ to, label, icon: I, bg, id }) => (
              <Link key={to} to={to} className="jt-btn text-white text-sm hover:-translate-y-0.5 hover:shadow-lg" style={{ background: bg }} data-testid={id}><I className="w-5 h-5 shrink-0" /> {label}</Link>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="jt-card p-5">
              <SectionTitle right={<Link to="/stok" className="text-sm font-semibold text-[#2357c9]" data-testid="dash-stock-link">Lihat Semua</Link>}>Stok Bahan Baku</SectionTitle>
              <div className="divide-y divide-[var(--jt-gold)]/15">
                {d.stock.map((s) => (
                  <div key={s.key} className="flex items-center gap-3 py-3" data-testid={`dash-stock-${s.key}`}>
                    <span className="flex-1 font-semibold">{s.name}</span>
                    <span className="font-bold tabular-nums">{qty(s.qty)} {s.unit}</span>
                    <StatusBadge status={s.status} />
                  </div>
                ))}
              </div>
            </div>
            <div className="jt-card p-5">
              <SectionTitle>Aktivitas Terbaru</SectionTitle>
              {d.recent.length === 0 ? <EmptyState text="Belum ada aktivitas. Mulai catat transaksi pertama Anda." /> :
                <div className="divide-y divide-[var(--jt-gold)]/15">{d.recent.map((t) => <TxRow key={t.id} t={t} mats={mats} testid="dash-recent-row" />)}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
